<script lang="ts">
    import type { AnalysisResult } from '../api/analysis';
    import { placeOrder, type PlaceOrderResponse } from '../api/trade';
    import { fetchSmartMoney, type SmartMoneyData } from '../api/smartMoney';
    import { authToken } from '../store.svelte';
    import { get } from 'svelte/store';
    import { slide } from 'svelte/transition';

    interface Params {
        result:        AnalysisResult | null;
        loading:       boolean;
        onAnalyze:     () => void;
        currentPrice?: number;
        balanceUsdt?:  number;
        symbol?:       string;
    }
    let { result, loading, onAnalyze, currentPrice = 0, balanceUsdt = 0, symbol = '' }: Params = $props();

    // ── Smart Money ───────────────────────────────────────
    let smData    = $state<SmartMoneyData | null>(null);
    let smLoading = $state(false);
    let smOpen    = $state(false);

    const smSymbol = $derived(result?.symbol ?? symbol);

    $effect(() => {
        if (!smSymbol || !isLoggedIn()) return;
        smLoading = true;
        fetchSmartMoney(smSymbol)
            .then(d => { smData = d; })
            .catch(() => { smData = null; })
            .finally(() => { smLoading = false; });
    });

    let expanded       = $state(false);
    let tradeOpen      = $state(false);
    let confirmOpen    = $state(false);
    let placing        = $state(false);
    let placeResult    = $state<PlaceOrderResponse | null>(null);
    let orderType      = $state<'MARKET' | 'LIMIT'>('MARKET');
    // Редаговані параметри AI-рекомендації
    let tradeLeverage  = $state(2);
    let tradeSizeUsdt  = $state(100);

    // ── Override (wait-режим) ─────────────────────────
    let ovOpen        = $state(false);
    let ovWarned      = $state(false);
    let ovDirection   = $state<'long' | 'short'>('long');
    let ovRiskPct     = $state(1.5);   // % від балансу на ризик
    let ovSlPct       = $state(2.0);   // % стоп від ціни входу
    let ovTpPct       = $state(4.0);   // % тейк від ціни входу
    let ovConfirmOpen = $state(false);
    let ovPlacing     = $state(false);
    let ovPlaceResult = $state<PlaceOrderResponse | null>(null);

    let suggestedDirection = $derived.by<'long' | 'short'>(() => {
        if (!result) return 'long';
        const vals = Object.values(result.timeframe_bias ?? {});
        const bulls = vals.filter(b => b === 'bullish').length;
        const bears = vals.filter(b => b === 'bearish').length;
        return bulls >= bears ? 'long' : 'short';
    });

    // ── Авто-розрахунок ризик-менеджменту ────────────
    // risk_usdt  = balance × risk%
    // size_usdt  = risk_usdt / sl%          (notional щоб втратити рівно risk_usdt при SL)
    // leverage   = ceil(size_usdt / balance), обмежено 1–20x
    let ovRiskUsdt = $derived(balanceUsdt > 0 ? balanceUsdt * ovRiskPct / 100 : 0);
    let ovSizeUsdt = $derived(ovSlPct > 0 && balanceUsdt > 0
        ? ovRiskUsdt / (ovSlPct / 100)
        : 100
    );
    let ovLeverage = $derived(balanceUsdt > 0
        ? Math.max(1, Math.min(20, Math.ceil(ovSizeUsdt / balanceUsdt)))
        : 2
    );

    let ovEntry = $derived(currentPrice);
    let ovSl    = $derived(
        ovDirection === 'long'
            ? currentPrice * (1 - ovSlPct / 100)
            : currentPrice * (1 + ovSlPct / 100)
    );
    let ovTp    = $derived(
        ovDirection === 'long'
            ? currentPrice * (1 + ovTpPct / 100)
            : currentPrice * (1 - ovTpPct / 100)
    );

    $effect(() => {
        if (result) {
            confirmOpen   = false;
            placeResult   = null;
            ovOpen        = false;
            ovWarned      = false;
            ovConfirmOpen = false;
            ovPlaceResult = null;
            ovDirection   = suggestedDirection;
            tradeLeverage = result.trade?.leverage  ?? 2;
            tradeSizeUsdt = result.trade?.position_size_usdt ?? 100;
        }
    });

    $effect(() => {
        if (result) {
            expanded  = true;
            tradeOpen = result.direction !== 'wait' && !!result.trade;
        }
    });

    async function openOverridePosition() {
        if (ovPlacing || !result) return;
        ovPlacing     = true;
        ovPlaceResult = null;
        try {
            ovPlaceResult = await placeOrder({
                symbol:             result.symbol,
                direction:          ovDirection,
                entry:              ovEntry,
                stop_loss:          ovSl,
                take_profit:        [ovTp],
                leverage:           ovLeverage,
                position_size_usdt: ovSizeUsdt,
                order_type:         'MARKET',
            });
        } catch (e: unknown) {
            ovPlaceResult = { success: false, error: e instanceof Error ? e.message : String(e) };
        } finally {
            ovPlacing     = false;
            ovConfirmOpen = false;
        }
    }

    async function openPosition() {
        if (!result?.trade || placing) return;
        placing = true;
        placeResult = null;
        try {
            placeResult = await placeOrder({
                symbol:             result.symbol,
                direction:          result.direction as 'long' | 'short',
                entry:              result.trade.entry,
                stop_loss:          result.trade.stop_loss,
                take_profit:        result.trade.take_profit,
                leverage:           tradeLeverage,
                position_size_usdt: tradeSizeUsdt,
                order_type:         orderType,
            });
        } catch (e: unknown) {
            placeResult = { success: false, error: e instanceof Error ? e.message : String(e) };
        } finally {
            placing    = false;
            confirmOpen = false;
        }
    }

    function isLoggedIn() {
        return !!get(authToken);
    }

    function directionLabel(d: string) {
        if (d === 'long')  return 'LONG ▲';
        if (d === 'short') return 'SHORT ▼';
        return 'ОЧІКУВАТИ';
    }
    function directionColor(d: string) {
        if (d === 'long')  return '#2e7d32';
        if (d === 'short') return '#c62828';
        return '#546e7a';
    }
    function directionBg(d: string) {
        if (d === 'long')  return '#e8f5e9';
        if (d === 'short') return '#ffebee';
        return '#f5f5f5';
    }
    function confidenceColor(c: number) {
        if (c >= 75) return '#2e7d32';
        if (c >= 50) return '#f57f17';
        return '#c62828';
    }
    function biasLabel(b: string) {
        if (b === 'bullish') return '▲';
        if (b === 'bearish') return '▼';
        return '—';
    }
    function biasColor(b: string) {
        if (b === 'bullish') return '#2e7d32';
        if (b === 'bearish') return '#c62828';
        return '#546e7a';
    }
    function cachedLabel(ts: number): string {
        const mins = Math.round((Date.now() / 1000 - ts) / 60);
        return mins < 1 ? 'щойно' : `${mins} хв тому`;
    }
    function fmt(v: number): string {
        if (v >= 1000) return v.toLocaleString('en', { maximumFractionDigits: 1 });
        if (v >= 10)   return v.toFixed(2);
        return v.toFixed(4);
    }
</script>

<div class="ap-wrap">
    <!-- Кнопка запуску -->
    <button
        class="ap-btn"
        class:loading
        onclick={onAnalyze}
        disabled={loading}
        title="AI аналіз монети"
    >
        {#if loading}
            <span class="ap-spinner"></span> Аналіз...
        {:else}
            ✦ AI
        {/if}
    </button>

    <!-- Smart Money картка -->
    {#if smSymbol && isLoggedIn()}
        <div class="ap-sm-wrap">
            <button class="ap-sm-header" onclick={() => smOpen = !smOpen}
                title="Дані про позиції великих трейдерів на Binance ф'ючерсах. Зелений = бичачий сигнал, червоний = ведмежий.">
                <span class="ap-sm-title">Smart Money</span>
                {#if smLoading}
                    <span class="ap-spinner ap-sm-spin"></span>
                {:else if smData}
                    {@const tt = smData.top_traders}
                    {@const oi = smData.open_interest}
                    {@const tk = smData.taker_flow}
                    <span class="ap-sm-pill" class:bear={tt?.trend === 'increasing_shorts'} class:bull={tt?.trend === 'increasing_longs'}
                        title="Позиції топ-трейдерів (зважені за обсягом контракту).&#10;Зелений: топи нарощують лонги — бичачий сигнал.&#10;Червоний: топи нарощують шорти — ведмежий сигнал.&#10;{tt?.long_pct}% капіталу у лонг, {tt?.short_pct}% у шорт.">
                        {#if tt && !tt.error}
                            Топ {tt.long_pct}%↑/{tt.short_pct}%↓
                        {/if}
                    </span>
                    <span class="ap-sm-pill" class:bear={oi?.trend === 'distribution'} class:bull={oi?.trend === 'accumulation'}
                        title="Зміна відкритого інтересу за 4 години.&#10;Зростання OI = нові позиції відкриваються (накопичення).&#10;Падіння OI = позиції закриваються (розподіл/виходи).&#10;Зелений: накопичення. Червоний: розподіл.">
                        {#if oi && !oi.error}
                            OI {oi.change_4h_pct != null ? (oi.change_4h_pct > 0 ? '+' : '') + oi.change_4h_pct + '%' : '—'} 4h
                        {/if}
                    </span>
                    <span class="ap-sm-pill" class:bear={tk?.dominant === 'sellers'} class:bull={tk?.dominant === 'buyers'}
                        title="Taker Buy/Sell Ratio — співвідношення ринкових покупок до продажів.&#10;Ratio > 1: покупці агресивніші (бичаче).&#10;Ratio < 1: продавці агресивніші (ведмеже).&#10;Поточний ratio: {tk?.ratio}">
                        {#if tk && !tk.error}
                            Taker {tk.ratio}
                        {/if}
                    </span>
                {/if}
                <span class="ap-chevron">{smOpen ? '▲' : '▼'}</span>
            </button>

            {#if smOpen && smData}
                <div class="ap-sm-body" transition:slide={{ duration: 180 }}>
                    <!-- Топ-трейдери (позиції) -->
                    {#if smData.top_traders && !smData.top_traders.error}
                        <div class="ap-sm-row"
                            title="Binance Top Trader Long/Short Position Ratio.&#10;Розраховується як зважена сума позицій за обсягом — великі рахунки мають більшу вагу.&#10;Зелений колір бару = частка лонгів, червоний = шортів.&#10;24h зміна ratio: зростання = топи набирають лонги, падіння = набирають шорти.">
                            <span class="ap-sm-label">Топ позиції (обсяг)</span>
                            <span class="ap-sm-bar-wrap" title="Зелений = лонги, червоний = шорти">
                                <span class="ap-sm-bar-bull" style="width:{smData.top_traders.long_pct ?? 50}%"></span>
                                <span class="ap-sm-bar-bear" style="width:{smData.top_traders.short_pct ?? 50}%"></span>
                            </span>
                            <span class="ap-sm-val bull"
                                title="Частка капіталу топ-трейдерів у лонгових позиціях">{smData.top_traders.long_pct}%↑</span>
                            <span class="ap-sm-val bear"
                                title="Частка капіталу топ-трейдерів у шортових позиціях">{smData.top_traders.short_pct}%↓</span>
                            {#if smData.top_traders.ratio_24h_change_pct != null}
                                <span class="ap-sm-chg"
                                    class:positive={smData.top_traders.ratio_24h_change_pct > 0}
                                    class:negative={smData.top_traders.ratio_24h_change_pct < 0}
                                    title="Зміна long/short ratio за 24 години.&#10;+ = топи нарощують лонги (бичаче).&#10;− = топи нарощують шорти (ведмеже).">
                                    {smData.top_traders.ratio_24h_change_pct > 0 ? '+' : ''}{smData.top_traders.ratio_24h_change_pct}% 24h
                                </span>
                            {/if}
                        </div>
                    {/if}

                    <!-- Топ-акаунти -->
                    {#if smData.top_accounts && !smData.top_accounts.error}
                        <div class="ap-sm-row"
                            title="Binance Top Trader Long/Short Account Ratio.&#10;Скільки відсотків топ-акаунтів (за кількістю) мають відкриті лонги або шорти.&#10;Відрізняється від 'Топ позиції': тут кожен акаунт = 1 голос, незалежно від розміру депозиту.&#10;Порівняння двох метрик: якщо 'позиції' і 'акаунти' розходяться — великі гравці діють інакше ніж дрібні топи.">
                            <span class="ap-sm-label">Топ акаунти (кількість)</span>
                            <span class="ap-sm-bar-wrap" title="Зелений = лонги, червоний = шорти">
                                <span class="ap-sm-bar-bull" style="width:{smData.top_accounts.long_pct ?? 50}%"></span>
                                <span class="ap-sm-bar-bear" style="width:{smData.top_accounts.short_pct ?? 50}%"></span>
                            </span>
                            <span class="ap-sm-val bull"
                                title="% акаунтів у лонзі">{smData.top_accounts.long_pct}%↑</span>
                            <span class="ap-sm-val bear"
                                title="% акаунтів у шорті">{smData.top_accounts.short_pct}%↓</span>
                        </div>
                    {/if}

                    <!-- Open Interest -->
                    {#if smData.open_interest && !smData.open_interest.error}
                        <div class="ap-sm-row ap-sm-row--oi"
                            title="Open Interest — загальний обсяг відкритих контрактів у USDT.&#10;↑ Зростання OI + ціна зростає = підтвердження бичачого тренду.&#10;↑ Зростання OI + ціна падає = підтвердження ведмежого тренду.&#10;↓ Падіння OI = закриття позицій, можливий розворот або консолідація.&#10;Зелений: накопичення (OI зростає >2% за 4h). Червоний: розподіл (OI падає >2%).">
                            <span class="ap-sm-label">Open Interest</span>
                            <span class="ap-sm-oi-val"
                                title="Загальний відкритий інтерес у доларах">
                                {smData.open_interest.current_usdt != null
                                    ? '$' + (smData.open_interest.current_usdt / 1e6).toFixed(1) + 'M'
                                    : '—'}
                            </span>
                            <span class="ap-sm-chg"
                                class:positive={smData.open_interest.change_4h_pct != null && smData.open_interest.change_4h_pct > 0}
                                class:negative={smData.open_interest.change_4h_pct != null && smData.open_interest.change_4h_pct < 0}
                                title="Зміна OI за останні 4 години. Позитивне = нові позиції відкриваються.">
                                4h: {smData.open_interest.change_4h_pct != null
                                    ? (smData.open_interest.change_4h_pct > 0 ? '+' : '') + smData.open_interest.change_4h_pct + '%'
                                    : '—'}
                            </span>
                            <span class="ap-sm-chg"
                                class:positive={smData.open_interest.change_24h_pct != null && smData.open_interest.change_24h_pct > 0}
                                class:negative={smData.open_interest.change_24h_pct != null && smData.open_interest.change_24h_pct < 0}
                                title="Зміна OI за 24 години.">
                                24h: {smData.open_interest.change_24h_pct != null
                                    ? (smData.open_interest.change_24h_pct > 0 ? '+' : '') + smData.open_interest.change_24h_pct + '%'
                                    : '—'}
                            </span>
                            <span class="ap-sm-trend"
                                class:bull={smData.open_interest.trend === 'accumulation'}
                                class:bear={smData.open_interest.trend === 'distribution'}
                                title="Накопичення: OI зростає >2% за 4h — великі гравці набирають позиції.&#10;Розподіл: OI падає >2% за 4h — позиції закриваються (виходи або ліквідації).&#10;Нейтрально: зміна в межах ±2%.">
                                {smData.open_interest.trend === 'accumulation' ? '↑ Накопичення'
                                    : smData.open_interest.trend === 'distribution' ? '↓ Розподіл'
                                    : '→ Нейтрально'}
                            </span>
                        </div>
                    {/if}

                    <!-- Taker Flow -->
                    {#if smData.taker_flow && !smData.taker_flow.error}
                        <div class="ap-sm-row"
                            title="Taker Buy/Sell Ratio — агресія покупців vs продавців за 24 години.&#10;Taker = той хто бере ціну з книги (ринковий ордер).&#10;Ratio > 1: покупці агресивніші — ведмежий тренд тяжко утримати.&#10;Ratio < 1: продавці агресивніші — тиск донизу сильніший.&#10;Зелений бар = обсяг покупок, червоний = продажів.">
                            <span class="ap-sm-label">Taker Flow</span>
                            <span class="ap-sm-bar-wrap" title="Зелений = обсяг market-buy за 24h, червоний = market-sell">
                                <span class="ap-sm-bar-bull" style="width:{
                                    (smData.taker_flow.buy_vol_24h / (smData.taker_flow.buy_vol_24h + smData.taker_flow.sell_vol_24h) * 100).toFixed(0)
                                }%"></span>
                                <span class="ap-sm-bar-bear" style="width:{
                                    (smData.taker_flow.sell_vol_24h / (smData.taker_flow.buy_vol_24h + smData.taker_flow.sell_vol_24h) * 100).toFixed(0)
                                }%"></span>
                            </span>
                            <span class="ap-sm-val"
                                class:bull={smData.taker_flow.dominant === 'buyers'}
                                class:bear={smData.taker_flow.dominant === 'sellers'}
                                title="Buy/Sell ratio. >1 = покупці домінують, <1 = продавці домінують.">
                                ratio {smData.taker_flow.ratio}
                            </span>
                            <span class="ap-sm-trend"
                                class:bull={smData.taker_flow.dominant === 'buyers'}
                                class:bear={smData.taker_flow.dominant === 'sellers'}
                                title="Хто агресивніший на ринку за останні 24 години.">
                                {smData.taker_flow.dominant === 'buyers' ? '↑ Покупці домінують' : '↓ Продавці домінують'}
                            </span>
                        </div>
                    {/if}
                </div>
            {/if}
        </div>
    {/if}

    <!-- Результат -->
    {#if result}
        <div class="ap-result">
            <!-- Компактний рядок -->
            <button
                class="ap-summary-row"
                onclick={() => expanded = !expanded}
                style="border-left: 3px solid {directionColor(result.direction)}"
            >
                <span class="ap-direction" style="color:{directionColor(result.direction)}">
                    {directionLabel(result.direction)}
                </span>
                {#if result.ai_provider === 'gemini'}
                    <span class="ap-provider ap-provider--gemini" title="Google Gemini">Gemini</span>
                {:else}
                    <span class="ap-provider" title="Anthropic Claude">Claude</span>
                {/if}
                <span class="ap-confidence" style="color:{confidenceColor(result.confidence)}">
                    {result.confidence}%
                </span>
                <span class="ap-summary-text" title={result.summary}>{result.summary}</span>
                {#if result.cached_at}
                    <span class="ap-cached" title="Результат з кешу">↺ {cachedLabel(result.cached_at)}</span>
                {/if}
                <span class="ap-chevron">{expanded ? '▲' : '▼'}</span>
            </button>

            <!-- Деталі аналізу -->
            {#if expanded}
                <div class="ap-details" transition:slide={{ duration: 200 }}>
                    <!-- TF Bias -->
                    {#if Object.keys(result.timeframe_bias).length > 0}
                        <div class="ap-bias-row">
                            {#each Object.entries(result.timeframe_bias) as [tf, bias]}
                                <span class="ap-bias-item" style="color:{biasColor(bias)}">
                                    {tf} {biasLabel(bias)}
                                </span>
                            {/each}
                        </div>
                    {/if}

                    <!-- Сигнали -->
                    <div class="ap-signals">
                        {#if result.signals_bullish.length}
                            <div class="ap-signal-col">
                                <div class="ap-signal-title bull">Бичачі сигнали</div>
                                {#each result.signals_bullish as s}
                                    <div class="ap-signal-item">▲ {s}</div>
                                {/each}
                            </div>
                        {/if}
                        {#if result.signals_bearish.length}
                            <div class="ap-signal-col">
                                <div class="ap-signal-title bear">Ведмежі сигнали</div>
                                {#each result.signals_bearish as s}
                                    <div class="ap-signal-item">▼ {s}</div>
                                {/each}
                            </div>
                        {/if}
                    </div>

                    <!-- Аргументи -->
                    {#if result.reasoning.length}
                        <div class="ap-section-title">Аргументи</div>
                        <ul class="ap-reasoning">
                            {#each result.reasoning as r}
                                <li>{r}</li>
                            {/each}
                        </ul>
                    {/if}

                    <!-- Інвалідація -->
                    {#if result.invalidation}
                        <div class="ap-invalidation">
                            ⚠ Сигнал скасовується: {result.invalidation}
                        </div>
                    {/if}

                    <!-- Помилка -->
                    {#if !result.success && result.error}
                        <div class="ap-error">Помилка: {result.error}</div>
                    {/if}
                </div>
            {/if}

            <!-- ── Override: відкрити попри "wait" ── -->
            {#if result.direction === 'wait' && isLoggedIn()}
                <div class="ap-override-section">
                    {#if !ovOpen}
                        <button class="ap-override-trigger" onclick={() => ovOpen = true}>
                            ↗ Відкрити позицію вручну
                        </button>

                    {:else if !ovWarned}
                        <div class="ap-override-warning" transition:slide={{ duration: 150 }}>
                            <div class="ap-override-warn-icon">⚠</div>
                            <div class="ap-override-warn-body">
                                <strong>AI рекомендує очікувати</strong>
                                <div class="ap-override-warn-text">
                                    Ринкові умови не оптимальні. Відкриваючи позицію зараз — дієте на власний ризик.
                                </div>
                                {#if result.signals_bullish.length || result.signals_bearish.length}
                                    <div class="ap-override-signals">
                                        <span class="ap-ov-sig-bull">▲ {result.signals_bullish.length} бичачих</span>
                                        <span class="ap-ov-sig-bear">▼ {result.signals_bearish.length} ведмежих</span>
                                    </div>
                                {/if}
                                <div class="ap-override-warn-btns">
                                    <button class="ap-override-proceed" onclick={() => ovWarned = true}>
                                        Продовжити на власний ризик →
                                    </button>
                                    <button class="ap-override-cancel" onclick={() => ovOpen = false}>
                                        Скасувати
                                    </button>
                                </div>
                            </div>
                        </div>

                    {:else}
                        <div class="ap-override-form" transition:slide={{ duration: 150 }}>
                            <!-- Вибір напрямку -->
                            <div class="ap-ov-dir-row">
                                <button
                                    class="ap-ov-dir-btn ap-ov-dir-long"
                                    class:active={ovDirection === 'long'}
                                    onclick={() => ovDirection = 'long'}
                                >LONG ▲</button>
                                <button
                                    class="ap-ov-dir-btn ap-ov-dir-short"
                                    class:active={ovDirection === 'short'}
                                    onclick={() => ovDirection = 'short'}
                                >SHORT ▼</button>
                                <span class="ap-ov-suggest">
                                    {suggestedDirection === 'long' ? '▲ більше бичачих сигналів' : '▼ більше ведмежих сигналів'}
                                </span>
                            </div>

                            <!-- Параметри (ризик-менеджмент) -->
                            <div class="ap-ov-params">
                                <label class="ap-ov-param">
                                    <span class="ap-ov-param-label">Ризик</span>
                                    <input class="ap-ov-input" type="number" bind:value={ovRiskPct} min="0.1" max="10" step="0.1" />
                                    <span class="ap-ov-param-unit">%</span>
                                </label>
                                <label class="ap-ov-param">
                                    <span class="ap-ov-param-label">SL від входу</span>
                                    <input class="ap-ov-input" type="number" bind:value={ovSlPct} min="0.1" max="20" step="0.1" />
                                    <span class="ap-ov-param-unit">%</span>
                                </label>
                                <label class="ap-ov-param">
                                    <span class="ap-ov-param-label">TP від входу</span>
                                    <input class="ap-ov-input" type="number" bind:value={ovTpPct} min="0.1" max="50" step="0.1" />
                                    <span class="ap-ov-param-unit">%</span>
                                </label>
                            </div>

                            <!-- Авто-розраховані значення -->
                            <div class="ap-ov-auto">
                                <div class="ap-ov-auto-item">
                                    <span class="ap-ov-auto-label">Ризик USDT</span>
                                    <span class="ap-ov-auto-val ap-tc-stop">{ovRiskUsdt > 0 ? ovRiskUsdt.toFixed(2) : '—'}</span>
                                </div>
                                <div class="ap-ov-auto-item">
                                    <span class="ap-ov-auto-label">Розмір позиції</span>
                                    <span class="ap-ov-auto-val">{ovSizeUsdt > 0 ? ovSizeUsdt.toFixed(0) + ' USDT' : '—'}</span>
                                </div>
                                <div class="ap-ov-auto-item">
                                    <span class="ap-ov-auto-label">Плече</span>
                                    <span class="ap-ov-auto-val">{ovLeverage}x</span>
                                </div>
                                <div class="ap-ov-auto-item">
                                    <span class="ap-ov-auto-label">R:R</span>
                                    <span class="ap-ov-auto-val ap-tc-tp">1:{(ovTpPct / ovSlPct).toFixed(1)}</span>
                                </div>
                            </div>

                            <!-- Ціни входу / SL / TP -->
                            {#if currentPrice > 0}
                                <div class="ap-ov-levels">
                                    <span>Вхід: <strong>{fmt(ovEntry)}</strong></span>
                                    <span class="ap-tc-stop">SL: {fmt(ovSl)}</span>
                                    <span class="ap-tc-tp">TP: {fmt(ovTp)}</span>
                                </div>
                                {#if balanceUsdt <= 0}
                                    <div class="ap-ov-levels ap-ov-levels--warn">⚠ Баланс невідомий — розміри орієнтовні</div>
                                {/if}
                                {#if ovSizeUsdt < 100}
                                    <div class="ap-ov-levels ap-ov-levels--warn">⚠ Мін. розмір Binance — 100 USDT. Збільшіть ризик або зменшіть SL%</div>
                                {/if}
                            {:else}
                                <div class="ap-ov-levels ap-ov-levels--warn">⚠ Поточна ціна недоступна</div>
                            {/if}

                            <!-- Відкрити / підтвердити -->
                            {#if !ovConfirmOpen && !ovPlaceResult}
                                <div class="ap-open-row">
                                    <button
                                        class="ap-open-btn"
                                        style="background:{directionColor(ovDirection)}"
                                        onclick={() => ovConfirmOpen = true}
                                        disabled={currentPrice <= 0}
                                    >
                                        ▶ Відкрити {ovDirection === 'long' ? 'LONG' : 'SHORT'}
                                    </button>
                                    <button class="ap-override-cancel" onclick={() => { ovOpen = false; ovWarned = false; }}>↩</button>
                                </div>
                            {/if}

                            {#if ovConfirmOpen}
                                <div class="ap-confirm" transition:slide={{ duration: 150 }}>
                                    <div class="ap-confirm-text">
                                        <strong>{result.symbol} {ovDirection.toUpperCase()} MARKET</strong><br>
                                        Позиція: {ovSizeUsdt} USDT · Плече: {ovLeverage}x<br>
                                        SL: <span class="ap-tc-stop">{fmt(ovSl)}</span>&nbsp;
                                        TP: <span class="ap-tc-tp">{fmt(ovTp)}</span>
                                    </div>
                                    <div class="ap-confirm-btns">
                                        <button
                                            class="ap-confirm-yes"
                                            style="background:{directionColor(ovDirection)}"
                                            onclick={openOverridePosition}
                                            disabled={ovPlacing}
                                        >
                                            {#if ovPlacing}<span class="ap-spinner"></span>&nbsp;{/if}
                                            ✓ Підтвердити
                                        </button>
                                        <button class="ap-confirm-no" onclick={() => ovConfirmOpen = false} disabled={ovPlacing}>
                                            ✗ Скасувати
                                        </button>
                                    </div>
                                </div>
                            {/if}

                            {#if ovPlaceResult}
                                {#if ovPlaceResult.success}
                                    <div class="ap-place-ok" transition:slide={{ duration: 150 }}>
                                        ✓ Позицію відкрито!
                                        {#if ovPlaceResult.entry_order}Ордер #{ovPlaceResult.entry_order.orderId}{/if}
                                        <button class="ap-place-dismiss" onclick={() => ovPlaceResult = null}>×</button>
                                    </div>
                                {:else}
                                    <div class="ap-place-err" transition:slide={{ duration: 150 }}>
                                        ✗ {ovPlaceResult.error}
                                        <button class="ap-place-dismiss" onclick={() => { ovPlaceResult = null; ovConfirmOpen = false; }}>×</button>
                                    </div>
                                {/if}
                            {/if}
                        </div>
                    {/if}
                </div>
            {/if}

            <!-- Кнопка LONG / SHORT -->
            {#if result.direction !== 'wait' && result.trade}
                {@const t = result.trade}
                <div class="ap-trade-section">
                    <button
                        class="ap-trade-btn"
                        style="background:{directionBg(result.direction)};border-color:{directionColor(result.direction)};color:{directionColor(result.direction)}"
                        onclick={() => tradeOpen = !tradeOpen}
                    >
                        <span class="ap-trade-btn-label">{directionLabel(result.direction)}</span>
                        <span class="ap-trade-btn-sub">
                            вхід {fmt(t.entry)} · плече {tradeLeverage}x · ризик {t.risk_pct.toFixed(1)}%
                        </span>
                        <span class="ap-trade-btn-chevron">{tradeOpen ? '▲' : '▼'}</span>
                    </button>

                    {#if tradeOpen}
                        <div class="ap-trade-card" transition:slide={{ duration: 150 }}
                            style="border-color:{directionColor(result.direction)}">
                            <!-- Рядок 1: ціни -->
                            <div class="ap-tc-row">
                                <div class="ap-tc-item">
                                    <span class="ap-tc-label">Вхід</span>
                                    <span class="ap-tc-val">{fmt(t.entry)}</span>
                                </div>
                                <div class="ap-tc-item">
                                    <span class="ap-tc-label">Стоп</span>
                                    <span class="ap-tc-val ap-tc-stop">{fmt(t.stop_loss)}</span>
                                </div>
                                <div class="ap-tc-item">
                                    <span class="ap-tc-label">TP1</span>
                                    <span class="ap-tc-val ap-tc-tp">{fmt(t.take_profit[0])}</span>
                                </div>
                                {#if t.take_profit[1]}
                                    <div class="ap-tc-item">
                                        <span class="ap-tc-label">TP2</span>
                                        <span class="ap-tc-val ap-tc-tp2">{fmt(t.take_profit[1])}</span>
                                    </div>
                                {/if}
                            </div>
                            <!-- Рядок 2: параметри позиції -->
                            <div class="ap-tc-divider"></div>
                            <div class="ap-tc-row">
                                <div class="ap-tc-item">
                                    <span class="ap-tc-label">Плече</span>
                                    <div class="ap-tc-edit">
                                        <input
                                            class="ap-tc-input"
                                            type="number"
                                            bind:value={tradeLeverage}
                                            min="1" max="20" step="1"
                                            title="Змінити плече"
                                        />
                                        <span class="ap-tc-input-unit">x</span>
                                    </div>
                                </div>
                                <div class="ap-tc-item">
                                    <span class="ap-tc-label">Позиція</span>
                                    <div class="ap-tc-edit">
                                        <input
                                            class="ap-tc-input"
                                            type="number"
                                            bind:value={tradeSizeUsdt}
                                            min="1" step="1"
                                            title="Змінити розмір позиції"
                                        />
                                        <span class="ap-tc-input-unit">U</span>
                                    </div>
                                </div>
                                <div class="ap-tc-item">
                                    <span class="ap-tc-label">Ризик</span>
                                    <span class="ap-tc-val ap-tc-stop">
                                        {t.risk_usdt.toFixed(1)} USDT
                                        <span class="ap-tc-pct">({t.risk_pct.toFixed(1)}%)</span>
                                    </span>
                                </div>
                                <div class="ap-tc-item">
                                    <span class="ap-tc-label">R:R</span>
                                    <span class="ap-tc-val ap-tc-tp">{t.rr_ratio}</span>
                                </div>
                            </div>
                            {#if tradeSizeUsdt < 100}
                                <div class="ap-ov-levels ap-ov-levels--warn" style="margin:4px 0">
                                    ⚠ Мін. розмір Binance — 100 USDT. Поточний: {tradeSizeUsdt.toFixed(0)} USDT
                                </div>
                            {/if}

                            <!-- Відкрити позицію -->
                            <div class="ap-tc-divider"></div>
                            {#if isLoggedIn()}
                                {#if !confirmOpen && !placeResult}
                                    <div class="ap-open-row">
                                        <div class="ap-order-type">
                                            <button
                                                class="ap-ot-btn"
                                                class:active={orderType === 'MARKET'}
                                                onclick={() => orderType = 'MARKET'}
                                            >MARKET</button>
                                            <button
                                                class="ap-ot-btn"
                                                class:active={orderType === 'LIMIT'}
                                                onclick={() => orderType = 'LIMIT'}
                                            >LIMIT</button>
                                        </div>
                                        <button
                                            class="ap-open-btn"
                                            style="background:{directionColor(result.direction)}"
                                            onclick={() => confirmOpen = true}
                                        >
                                            ▶ Відкрити {result.direction === 'long' ? 'LONG' : 'SHORT'}
                                        </button>
                                    </div>
                                    {#if orderType === 'LIMIT'}
                                        <div class="ap-limit-note">
                                            LIMIT: ордер виставиться за ціною {fmt(t.entry)}. SL/TP потрібно встановити вручну після заповнення.
                                        </div>
                                    {/if}
                                {/if}

                                {#if confirmOpen}
                                    <div class="ap-confirm" transition:slide={{ duration: 150 }}>
                                        <div class="ap-confirm-text">
                                            <strong>{result.symbol} {result.direction.toUpperCase()} {orderType}</strong><br>
                                            Позиція: {tradeSizeUsdt.toFixed(0)} USDT · Плече: {tradeLeverage}x<br>
                                            {#if orderType === 'MARKET'}
                                                SL: <span class="ap-tc-stop">{fmt(t.stop_loss)}</span> &nbsp;
                                                TP: <span class="ap-tc-tp">{fmt(t.take_profit[0])}</span>
                                                {#if t.take_profit[1]}/ <span class="ap-tc-tp2">{fmt(t.take_profit[1])}</span>{/if}
                                            {:else}
                                                Ціна входу: <strong>{fmt(t.entry)}</strong> · SL/TP вручну після заповнення
                                            {/if}
                                        </div>
                                        <div class="ap-confirm-btns">
                                            <button
                                                class="ap-confirm-yes"
                                                style="background:{directionColor(result.direction)}"
                                                onclick={openPosition}
                                                disabled={placing}
                                            >
                                                {#if placing}<span class="ap-spinner"></span>&nbsp;{/if}
                                                ✓ Підтвердити
                                            </button>
                                            <button class="ap-confirm-no" onclick={() => confirmOpen = false} disabled={placing}>
                                                ✗ Скасувати
                                            </button>
                                        </div>
                                    </div>
                                {/if}

                                {#if placeResult}
                                    {#if placeResult.success}
                                        <div class="ap-place-ok" transition:slide={{ duration: 150 }}>
                                            ✓ Позицію відкрито!
                                            {#if placeResult.entry_order}
                                                Ордер #{placeResult.entry_order.orderId}
                                            {/if}
                                            · SL та TP виставлено
                                            <button class="ap-place-dismiss" onclick={() => placeResult = null}>×</button>
                                        </div>
                                    {:else}
                                        <div class="ap-place-err" transition:slide={{ duration: 150 }}>
                                            ✗ {placeResult.error}
                                            <button class="ap-place-dismiss" onclick={() => { placeResult = null; confirmOpen = false; }}>×</button>
                                        </div>
                                    {/if}
                                {/if}
                            {:else}
                                <div class="ap-no-auth">
                                    Увійдіть та налаштуйте Binance API ключі для відкриття позицій
                                </div>
                            {/if}
                        </div>
                    {/if}
                </div>
            {/if}
        </div>
    {/if}
</div>

<style>
    .ap-wrap {
        display: flex;
        flex-direction: column;
        gap: 4px;
        margin-top: 4px;
    }

    /* ── Smart Money ── */
    .ap-sm-wrap {
        border: 1px solid #e0e0e0;
        border-radius: 6px;
        overflow: hidden;
        font-size: 0.7rem;
    }
    .ap-sm-header {
        display: flex;
        align-items: center;
        gap: 5px;
        width: 100%;
        padding: 4px 7px;
        background: #f9f9f9;
        border: none;
        cursor: pointer;
        text-align: left;
        flex-wrap: wrap;
    }
    .ap-sm-title {
        font-weight: 600;
        font-size: 0.69rem;
        color: #444;
        margin-right: 2px;
    }
    .ap-sm-spin { width: 10px; height: 10px; }
    .ap-sm-pill {
        padding: 1px 5px;
        border-radius: 3px;
        background: #eee;
        color: #555;
        font-size: 0.67rem;
    }
    .ap-sm-pill.bull { background: #e8f5e9; color: #2e7d32; }
    .ap-sm-pill.bear { background: #ffebee; color: #c62828; }

    .ap-sm-body {
        padding: 6px 8px;
        display: flex;
        flex-direction: column;
        gap: 5px;
        background: #fff;
    }
    .ap-sm-row {
        display: flex;
        align-items: center;
        gap: 5px;
        flex-wrap: wrap;
    }
    .ap-sm-row--oi { align-items: baseline; }
    .ap-sm-label {
        font-size: 0.66rem;
        color: #666;
        min-width: 120px;
    }
    .ap-sm-bar-wrap {
        display: flex;
        height: 6px;
        border-radius: 3px;
        overflow: hidden;
        width: 80px;
        flex-shrink: 0;
    }
    .ap-sm-bar-bull { background: #4caf50; }
    .ap-sm-bar-bear { background: #ef5350; }
    .ap-sm-val {
        font-size: 0.67rem;
        color: #555;
    }
    .ap-sm-val.bull { color: #2e7d32; font-weight: 600; }
    .ap-sm-val.bear { color: #c62828; font-weight: 600; }
    .ap-sm-chg {
        font-size: 0.66rem;
        color: #888;
    }
    .ap-sm-chg.positive { color: #2e7d32; }
    .ap-sm-chg.negative { color: #c62828; }
    .ap-sm-oi-val {
        font-size: 0.67rem;
        font-weight: 600;
        color: #333;
    }
    .ap-sm-trend {
        font-size: 0.66rem;
        color: #666;
        margin-left: auto;
    }
    .ap-sm-trend.bull { color: #2e7d32; }
    .ap-sm-trend.bear { color: #c62828; }

    /* Кнопка AI */
    .ap-btn {
        display: inline-flex;
        align-items: center;
        gap: 5px;
        padding: 2px 8px;
        font-size: 0.72rem;
        font-weight: 600;
        border: 1px solid #9c27b0;
        color: #9c27b0;
        background: transparent;
        border-radius: 4px;
        cursor: pointer;
        transition: background 0.15s, color 0.15s;
        align-self: flex-end;
    }
    .ap-btn:hover:not(:disabled) { background: #9c27b0; color: #fff; }
    .ap-btn:disabled { opacity: 0.6; cursor: not-allowed; }
    .ap-btn.loading { border-color: #888; color: #888; }

    .ap-spinner {
        display: inline-block;
        width: 10px;
        height: 10px;
        border: 2px solid #bbb;
        border-top-color: #555;
        border-radius: 50%;
        animation: spin 0.7s linear infinite;
    }
    @keyframes spin { to { transform: rotate(360deg); } }

    /* Результат */
    .ap-result {
        font-size: 0.76rem;
        border: 1px solid #e0e0e0;
        border-radius: 6px;
        overflow: hidden;
    }

    .ap-summary-row {
        display: flex;
        align-items: center;
        gap: 6px;
        width: 100%;
        padding: 5px 8px;
        background: #fafafa;
        border: none;
        cursor: pointer;
        text-align: left;
        font-size: 0.76rem;
    }
    .ap-summary-row:hover { background: #f0f0f0; }

    .ap-direction   { font-weight: 700; font-size: 0.78rem; flex-shrink: 0; }
    .ap-confidence  { font-weight: 700; flex-shrink: 0; }
    .ap-summary-text{ flex: 1; color: #333; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .ap-chevron     { color: #999; font-size: 0.65rem; flex-shrink: 0; }
    .ap-cached      { color: #9e9e9e; font-size: 0.65rem; flex-shrink: 0; }
    .ap-provider    { font-size: 0.62rem; color: #bbb; flex-shrink: 0; font-weight: 500; }
    .ap-provider--gemini { color: #1a73e8; }

    /* Деталі */
    .ap-details { padding: 8px; background: #fff; display: flex; flex-direction: column; gap: 8px; }

    .ap-bias-row    { display: flex; gap: 10px; flex-wrap: wrap; }
    .ap-bias-item   { font-weight: 600; font-size: 0.75rem; }

    .ap-signals         { display: grid; grid-template-columns: 1fr 1fr; gap: 6px; }
    .ap-signal-col      { display: flex; flex-direction: column; gap: 2px; }
    .ap-signal-title    { font-weight: 600; font-size: 0.72rem; margin-bottom: 2px; }
    .ap-signal-title.bull { color: #2e7d32; }
    .ap-signal-title.bear { color: #c62828; }
    .ap-signal-item     { color: #444; font-size: 0.72rem; }

    .ap-section-title { font-weight: 600; font-size: 0.73rem; color: #555; margin-bottom: 2px; }

    .ap-reasoning    { margin: 0; padding-left: 14px; color: #333; display: flex; flex-direction: column; gap: 2px; }
    .ap-reasoning li { font-size: 0.73rem; }

    .ap-invalidation {
        font-size: 0.72rem;
        color: #f57f17;
        background: #fff8e1;
        border-radius: 4px;
        padding: 5px 8px;
    }
    .ap-error {
        font-size: 0.72rem;
        color: #c62828;
        background: #ffebee;
        border-radius: 4px;
        padding: 5px 8px;
    }

    /* ── Торгова секція ─────────────────────────────── */
    .ap-trade-section {
        border-top: 1px solid #e0e0e0;
    }

    .ap-trade-btn {
        display: flex;
        align-items: center;
        gap: 8px;
        width: 100%;
        padding: 7px 10px;
        border: none;
        border-top: 2px solid;
        cursor: pointer;
        text-align: left;
        transition: filter 0.15s;
    }
    .ap-trade-btn:hover { filter: brightness(0.95); }

    .ap-trade-btn-label {
        font-weight: 800;
        font-size: 0.85rem;
        flex-shrink: 0;
        letter-spacing: 0.5px;
    }
    .ap-trade-btn-sub {
        flex: 1;
        font-size: 0.7rem;
        opacity: 0.8;
    }
    .ap-trade-btn-chevron {
        font-size: 0.65rem;
        opacity: 0.6;
        flex-shrink: 0;
    }

    /* Картка торгових параметрів */
    .ap-trade-card {
        padding: 8px 10px;
        background: #fff;
        border-top: 2px solid;
        display: flex;
        flex-direction: column;
        gap: 6px;
    }

    .ap-tc-row {
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        gap: 4px;
    }
    .ap-tc-divider {
        height: 1px;
        background: #eeeeee;
    }
    .ap-tc-item {
        display: flex;
        flex-direction: column;
        gap: 1px;
        padding: 4px 6px;
        background: #f8f8f8;
        border-radius: 4px;
    }
    .ap-tc-label { font-size: 0.66rem; color: #999; }
    .ap-tc-val   { font-weight: 700; font-size: 0.78rem; color: #222; }
    .ap-tc-pct   { font-weight: 400; font-size: 0.68rem; color: #888; }
    .ap-tc-edit  { display: flex; align-items: center; gap: 2px; }
    .ap-tc-input {
        width: 52px;
        padding: 1px 4px;
        font-size: 0.78rem;
        font-weight: 700;
        border: 1px solid #ccc;
        border-radius: 3px;
        background: #fff;
        color: #222;
        text-align: right;
    }
    .ap-tc-input:focus { outline: none; border-color: #9c27b0; }
    .ap-tc-input-unit { font-size: 0.68rem; color: #888; }
    .ap-tc-stop  { color: #c62828; }
    .ap-tc-tp    { color: #2e7d32; }
    .ap-tc-tp2   { color: #1565c0; }

    /* Відкрити позицію */
    .ap-open-row {
        display: flex;
        gap: 6px;
        align-items: center;
    }
    .ap-order-type {
        display: flex;
        border: 1px solid #ddd;
        border-radius: 4px;
        overflow: hidden;
        flex-shrink: 0;
    }
    .ap-ot-btn {
        padding: 4px 8px;
        font-size: 0.68rem;
        font-weight: 600;
        border: none;
        background: #fff;
        color: #888;
        cursor: pointer;
        transition: all 0.15s;
    }
    .ap-ot-btn.active { background: #1976d2; color: #fff; }
    .ap-limit-note {
        font-size: 0.68rem;
        color: #f57f17;
        background: #fff8e1;
        border-radius: 4px;
        padding: 4px 6px;
    }
    .ap-open-btn {
        flex: 1;
        padding: 7px;
        border: none;
        border-radius: 4px;
        color: #fff;
        font-weight: 700;
        font-size: 0.78rem;
        cursor: pointer;
        letter-spacing: 0.3px;
        transition: filter 0.15s;
    }
    .ap-open-btn:hover { filter: brightness(1.1); }

    /* Підтвердження */
    .ap-confirm {
        background: #f9f9f9;
        border-radius: 4px;
        padding: 8px;
        display: flex;
        flex-direction: column;
        gap: 6px;
        font-size: 0.73rem;
    }
    .ap-confirm-text { color: #333; line-height: 1.6; }
    .ap-confirm-btns { display: flex; gap: 6px; }
    .ap-confirm-yes {
        flex: 1;
        padding: 5px;
        border: none;
        border-radius: 4px;
        color: #fff;
        font-weight: 700;
        font-size: 0.75rem;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 4px;
        transition: filter 0.15s;
    }
    .ap-confirm-yes:hover:not(:disabled) { filter: brightness(1.1); }
    .ap-confirm-yes:disabled { opacity: 0.6; cursor: not-allowed; }
    .ap-confirm-no {
        flex: 1;
        padding: 5px;
        border: 1px solid #ccc;
        border-radius: 4px;
        background: #fff;
        color: #555;
        font-size: 0.75rem;
        cursor: pointer;
    }
    .ap-confirm-no:hover:not(:disabled) { background: #f0f0f0; }
    .ap-confirm-no:disabled { opacity: 0.6; cursor: not-allowed; }

    /* Результат розміщення */
    .ap-place-ok {
        display: flex;
        align-items: center;
        gap: 6px;
        padding: 6px 8px;
        background: #e8f5e9;
        color: #2e7d32;
        font-size: 0.73rem;
        font-weight: 600;
        border-radius: 4px;
    }
    .ap-place-err {
        display: flex;
        align-items: center;
        gap: 6px;
        padding: 6px 8px;
        background: #ffebee;
        color: #c62828;
        font-size: 0.73rem;
        border-radius: 4px;
    }
    .ap-place-dismiss {
        margin-left: auto;
        background: none;
        border: none;
        cursor: pointer;
        font-size: 0.9rem;
        color: inherit;
        opacity: 0.6;
        padding: 0 2px;
    }
    .ap-place-dismiss:hover { opacity: 1; }

    .ap-no-auth {
        font-size: 0.68rem;
        color: #9e9e9e;
        text-align: center;
        padding: 4px;
    }

    /* ── Override (wait-режим) ───────────────────────── */
    .ap-override-section {
        border-top: 1px solid #e0e0e0;
    }

    .ap-override-trigger {
        width: 100%;
        padding: 6px 10px;
        border: none;
        background: #fafafa;
        cursor: pointer;
        font-size: 0.73rem;
        color: #888;
        text-align: left;
        transition: background 0.12s, color 0.12s;
    }
    .ap-override-trigger:hover { background: #f0f4ff; color: #1a56db; }

    .ap-override-warning {
        display: flex;
        gap: 8px;
        padding: 9px 10px;
        background: #fff8e1;
        border-top: 2px solid #f59e0b;
    }

    .ap-override-warn-icon {
        font-size: 1.1rem;
        flex-shrink: 0;
        line-height: 1.3;
    }

    .ap-override-warn-body {
        display: flex;
        flex-direction: column;
        gap: 4px;
        font-size: 0.74rem;
        color: #78350f;
    }

    .ap-override-warn-text {
        color: #92400e;
        font-size: 0.72rem;
    }

    .ap-override-signals {
        display: flex;
        gap: 8px;
        font-size: 0.7rem;
        font-weight: 600;
    }
    .ap-ov-sig-bull { color: #2e7d32; }
    .ap-ov-sig-bear { color: #c62828; }

    .ap-override-warn-btns {
        display: flex;
        gap: 6px;
        margin-top: 2px;
    }

    .ap-override-proceed {
        padding: 4px 9px;
        border: 1px solid #d97706;
        border-radius: 4px;
        background: #d97706;
        color: #fff;
        font-size: 0.7rem;
        font-weight: 700;
        cursor: pointer;
        transition: filter 0.12s;
    }
    .ap-override-proceed:hover { filter: brightness(1.1); }

    .ap-override-cancel {
        padding: 4px 8px;
        border: 1px solid #ccc;
        border-radius: 4px;
        background: #fff;
        font-size: 0.7rem;
        color: #666;
        cursor: pointer;
    }
    .ap-override-cancel:hover { background: #f0f0f0; }

    .ap-override-form {
        padding: 8px 10px;
        display: flex;
        flex-direction: column;
        gap: 7px;
        background: #fff;
        border-top: 2px solid #546e7a;
    }

    .ap-ov-dir-row {
        display: flex;
        align-items: center;
        gap: 5px;
        flex-wrap: wrap;
    }

    .ap-ov-dir-btn {
        padding: 4px 10px;
        border: 1px solid #ccc;
        border-radius: 4px;
        background: #fff;
        font-size: 0.72rem;
        font-weight: 700;
        cursor: pointer;
        transition: all 0.12s;
    }
    .ap-ov-dir-long.active  { background: #e8f5e9; border-color: #2e7d32; color: #2e7d32; }
    .ap-ov-dir-short.active { background: #ffebee; border-color: #c62828; color: #c62828; }

    .ap-ov-suggest {
        font-size: 0.66rem;
        color: #999;
        flex: 1;
    }

    .ap-ov-params {
        display: grid;
        grid-template-columns: 1fr 1fr 1fr 1fr;
        gap: 4px;
    }

    .ap-ov-param {
        display: flex;
        flex-direction: column;
        gap: 2px;
        background: #f8f8f8;
        border-radius: 4px;
        padding: 4px 6px;
    }

    .ap-ov-param-label {
        font-size: 0.63rem;
        color: #999;
    }

    .ap-ov-input {
        width: 100%;
        border: none;
        background: transparent;
        font-size: 0.78rem;
        font-weight: 700;
        color: #222;
        outline: none;
        padding: 0;
    }

    .ap-ov-param-unit {
        font-size: 0.6rem;
        color: #bbb;
    }

    .ap-ov-auto {
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        gap: 4px;
    }

    .ap-ov-auto-item {
        display: flex;
        flex-direction: column;
        gap: 1px;
        padding: 4px 6px;
        background: #f0f4ff;
        border-radius: 4px;
    }

    .ap-ov-auto-label {
        font-size: 0.62rem;
        color: #888;
    }

    .ap-ov-auto-val {
        font-weight: 700;
        font-size: 0.78rem;
        color: #222;
    }

    .ap-ov-levels {
        display: flex;
        gap: 10px;
        font-size: 0.72rem;
        flex-wrap: wrap;
    }

    .ap-ov-levels--warn {
        color: #f57f17;
        font-size: 0.7rem;
    }
</style>
